"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dropdown = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const framer_motion_1 = require("framer-motion");
const useDropdown_1 = require("./useDropdown");
const styles_1 = require("../../styles");
const getOptions = (options, onClickOption, currentSelection) => {
    return options.map((option, idx) => ((0, jsx_runtime_1.jsx)("li", Object.assign({ className: option.text === currentSelection ? 'active' : '' }, { children: (0, jsx_runtime_1.jsx)("button", Object.assign({ onClick: () => { onClickOption(option); } }, { children: option.text })) }), idx)));
};
const getInitialSelection = (options) => {
    var _a, _b;
    if ((_a = options[0]) === null || _a === void 0 ? void 0 : _a.currentVariantId) {
        const selected = options.filter((option) => option.variant.bigcommerce_id === option.currentVariantId)[0];
        return (_b = selected === null || selected === void 0 ? void 0 : selected.text) !== null && _b !== void 0 ? _b : options[0];
    }
    else {
        return options[0];
    }
};
exports.Dropdown = (0, react_1.memo)(({ className = '', trigger, options }) => {
    const [triggerRef, dropdownRef, expanded, toggleExpanded] = (0, useDropdown_1.useDropdown)();
    const [currentSelection, setSelection] = (0, react_1.useState)(getInitialSelection(options));
    const onClickOption = (option) => {
        var _a;
        toggleExpanded();
        setSelection((_a = option.text) !== null && _a !== void 0 ? _a : option);
        // Exception for variants
        option.setCurrentVariant && option.setCurrentVariant(option.variant);
    };
    const Options = (0, react_1.useMemo)(() => getOptions(options, onClickOption, currentSelection), [options]);
    return ((0, jsx_runtime_1.jsxs)(DropdownWrapper, Object.assign({ className: className }, { children: [(0, jsx_runtime_1.jsxs)("button", Object.assign({ className: 'triggerButton', ref: triggerRef, type: 'button', onClick: toggleExpanded }, { children: [trigger, (0, jsx_runtime_1.jsx)("span", Object.assign({ "data-expanded": expanded }, { children: currentSelection }))] })), (0, jsx_runtime_1.jsx)(OptionsList, Object.assign({ ref: dropdownRef, initial: 'collapsed', variants: optionsListVariants, animate: expanded ? 'expanded' : 'collapsed' }, { children: Options }))] })));
});
const DropdownWrapper = styled_components_1.default.div `
  .triggerButton {
    ${(0, styles_1.getP18_1)()}
    color: ${({ theme }) => (0, styles_1.hex2Rgba)(theme.colors.black, 0.5)};

    span {
      color: ${({ theme }) => theme.colors.black};
      position: relative;
      margin-left: ${(0, styles_1.vw)(10, 'mobile')};

      &::after {
        content: url('/images/svg/arrow-down.svg');
        display: inline-block;
        transition: transform 300ms ${({ theme }) => theme.ease};
        margin-left: ${(0, styles_1.vw)(10, 'mobile')};
      }

      &[data-expanded='true']::after {
        transform: rotate(-180deg);
      }
    }
  }

  ${styles_1.mq.greaterThan('nexus7')} {
    .triggerButton span {
      margin-left: ${(0, styles_1.vw)(10, 'nexus7')};

      &::after { margin-left: ${(0, styles_1.vw)(10, 'nexus7')}; }
    }
  }

  ${styles_1.mq.greaterThan('tablet')} {
    .triggerButton span {
      margin-left: ${(0, styles_1.vw)(10, 'desktop')};

      &::after { margin-left: ${(0, styles_1.vw)(10, 'desktop')}; }
    }
  }

  ${styles_1.mq.greaterThan('desktop')} {
    .triggerButton span {
      margin-left: 10px;

      &::after { margin-left: 10px; }
    }
  }
`;
const OptionsList = (0, styled_components_1.default)(framer_motion_1.motion.ul) `
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 4px 0 rgba(0,0,0, .2);
  right: 0;
  top: 100%;
  height: 0;
  opacity: 0;
  overflow-y: auto;
  padding: ${(0, styles_1.vw)(20, 'mobile')} ${(0, styles_1.vw)(30, 'mobile')} ${(0, styles_1.vw)(30, 'mobile')};
  position: absolute;
  width: auto;
  z-index: 10;
  ${(0, styles_1.hideScrollbar)()}

  ${styles_1.mq.greaterThan('nexus7')} {
    padding: ${(0, styles_1.vw)(20, 'nexus7')} ${(0, styles_1.vw)(30, 'nexus7')} ${(0, styles_1.vw)(30, 'nexus7')};
  }

  ${styles_1.mq.greaterThan('tablet')} {
    padding: ${(0, styles_1.vw)(20, 'desktop')} ${(0, styles_1.vw)(30, 'desktop')} ${(0, styles_1.vw)(30, 'desktop')};
  }

  ${styles_1.mq.greaterThan('desktop')} {
    padding: 20px 30px 30px;
  }

  li {
    cursor: pointer;
    margin-bottom: ${(0, styles_1.vw)(20, 'mobile')};
    opacity: .4;
    color: ${({ theme }) => theme.colors.black};
    width: 100%;
    background-color: ${({ theme }) => theme.colors.white};
    transition: opacity 300ms ${({ theme }) => theme.ease}, background-color 300ms ${({ theme }) => theme.ease};

    ${styles_1.mq.greaterThan('nexus7')} {
      margin-bottom: ${(0, styles_1.vw)(20, 'nexus7')};
    }

    ${styles_1.mq.greaterThan('tablet')} {
      margin-bottom: ${(0, styles_1.vw)(20, 'desktop')};
    }

    ${styles_1.mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &:active,
    &:hover,
    &.active {
      opacity: 1;
    }

    button {
      ${(0, styles_1.getP18_1)()}
    }
  }
`;
const optionsListVariants = {
    collapsed: {
        height: '0',
        opacity: 0,
        transitionEnd: {
            display: 'none',
        },
        transition: Object.assign({}, (0, styles_1.getTransition)())
    },
    expanded: {
        height: 'auto',
        opacity: 1,
        display: 'block',
        transition: Object.assign({}, (0, styles_1.getTransition)())
    }
};
